import React, { useEffect, useState } from 'react';
import { Card, List, Tag, Button, Input, Space, Tooltip, Select, Empty, Pagination, Spin, message } from 'antd';
import { PushpinOutlined, PushpinFilled, DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useNoteStore } from '../stores/noteStore';
import dayjs from 'dayjs';

// 定义分页限制常量，从环境变量读取，默认20
const pageLimit = parseInt(process.env.REACT_APP_FETCH_LIMIT || process.env.FETCH_LIMIT || '20', 10);

const { Search } = Input;
const { Option } = Select;

const Notes = () => {
  const navigate = useNavigate();
  const { notes, pinnedNotes, pagination, isLoading, fetchNotes, togglePinNote, deleteNote } = useNoteStore();
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  
  useEffect(() => {
    fetchNotes(1, pageLimit, selectedTags);
  }, [fetchNotes, selectedTags]);

  // 从所有笔记中提取唯一标签
  useEffect(() => {
    const allTags = [];
    
    [...pinnedNotes, ...notes].forEach(note => {
      if (note.tags && note.tags.length > 0) {
        note.tags.forEach(tag => {
          if (!allTags.find(t => t.value === tag.id)) {
            allTags.push({
              label: tag.name,
              value: tag.id,
              color: tag.color,
            });
          }
        });
      }
    });
    
    setTagOptions(allTags);
  }, [notes, pinnedNotes]);

  // 处理页面变化
  const handlePageChange = (page, pageSize) => {
    fetchNotes(page, pageSize, selectedTags);
  };

  // 处理置顶/取消置顶
  const handleTogglePin = async (id, isPinned) => {
    const result = await togglePinNote(id, isPinned);
    if (!result.success) {
      message.error(result.message);
    }
  };

  // 处理删除
  const handleDelete = async (id) => {
    const result = await deleteNote(id);
    if (result.success) {
      message.success('Note deleted');
      // 删除成功后刷新当前页，保持分页和筛选参数
      fetchNotes(pagination.page, pagination.limit, selectedTags);
    } else {
      message.error(result.message);
    }
  };

  // 渲染笔记列表项
  const renderNoteItem = (note) => (
    <List.Item
      key={note.id}
      actions={[
        <Tooltip title={note.is_pinned ? 'Unpin' : 'Pin'}>
          <Button 
            type="text" 
            icon={note.is_pinned ? <PushpinFilled /> : <PushpinOutlined />} 
            onClick={() => handleTogglePin(note.id, note.is_pinned)}
          />
        </Tooltip>,
        <Tooltip title="View details">
          <Button type="text" icon={<EyeOutlined />} onClick={() => navigate(`/notes/${note.id}`)} />
        </Tooltip>,
        <Tooltip title="Edit">
          <Button type="text" icon={<EditOutlined />} onClick={() => navigate(`/notes/${note.id}?edit=true`)} />
        </Tooltip>,
        <Tooltip title="Delete">
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(note.id)} />
        </Tooltip>,
      ]}
    >
      <List.Item.Meta
        title={
          <Space>
            {note.is_pinned && <PushpinFilled style={{ color: '#1890ff' }} />}
            <span onClick={() => navigate(`/notes/${note.id}`)} style={{ cursor: 'pointer' }}>
              {note.title}
            </span>
          </Space>
        }
        description={
          <div>
            <div style={{ marginBottom: 8 }}>
              {note.summary || note.content.substring(0, 100) + (note.content.length > 100 ? '...' : '')}
            </div>
            <div>
              <Space wrap>
                {note.tags?.map(tag => (
                  <Tag key={tag.id} color={tag.color || '#108ee9'} style={{ cursor: 'pointer' }}>
                    {tag.name}
                  </Tag>
                ))}
                <span style={{ color: '#8c8c8c', fontSize: '0.8rem' }}>
                  Created: {dayjs(note.created_at).format('YYYY-MM-DD HH:mm')}
                </span>
                {note.source_url && (
                  <a 
                    href={note.source_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.8rem' }}
                  >
                    View original
                  </a>
                )}
              </Space>
            </div>
          </div>
        }
      />
    </List.Item>
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Search 
              placeholder="Search notes..." 
              style={{ width: 300 }} 
              onSearch={() => navigate('/search')}
            />
            <Button type="primary" onClick={() => navigate('/create')}>Create Note</Button>
          </Space>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Filter by tags"
            value={selectedTags}
            onChange={setSelectedTags}
            optionLabelProp="label"
          >
            {tagOptions.map(tag => (
              <Option key={tag.value} value={tag.value} label={tag.label}>
                <Space>
                  <Tag color={tag.color}>{tag.label}</Tag>
                </Space>
              </Option>
            ))}
          </Select>
        </Space>
      </div>

      <Spin spinning={isLoading}>
        {pinnedNotes.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h3>Pinned Notes</h3>
            <List
              grid={{ gutter: 16, xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1 }}
              dataSource={pinnedNotes}
              renderItem={renderNoteItem}
            />
          </div>
        )}

        <div>
          <h3>All Notes</h3>
          {notes.length > 0 ? (
            <List
              grid={{ gutter: 16, xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1 }}
              dataSource={notes}
              renderItem={renderNoteItem}
            />
          ) : (
            <Card>
              <Empty description="No notes yet" />
            </Card>
          )}
        </div>

        {pagination.total > 0 && (
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Pagination
              current={pagination.page}
              pageSize={pagination.limit}
              total={pagination.total}
              onChange={handlePageChange}
              showSizeChanger
              showQuickJumper
              showTotal={(total) => `${total} notes total`}
            />
          </div>
        )}
      </Spin>
    </div>
  );
};

export default Notes; 